import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { sessionResponseSchema, type SessionResponse } from '@bekebi/contracts/api';
import type { LoginRequest } from '@bekebi/contracts/common';
import { apiRequest, isUnauthenticated } from '../api/client';
import { SESSION_QUERY_KEY } from './session';

/**
 * Sign-in and sign-out, using only the existing endpoints:
 *   POST /api/auth/login   → sets the httpOnly session cookie and returns the session
 *   POST /api/auth/logout  → revokes the session and clears the cookie
 * No credentials or tokens are kept in the browser: the cookie is httpOnly, and the password
 * exists only inside the login form until it is submitted.
 */

/** Marks auth-flow mutations whose 401 means "wrong credentials", not "session ended". */
export const AUTH_FLOW_META = { authFlow: true } as const;

/**
 * Why the user left the signed-in area; shown as a short notice on /login. Kept in the query cache
 * (memory only) so the route guard that performs the redirect can pass it on.
 */
export const SIGNED_OUT_REASON_KEY = ['signed-out-reason'] as const;

/** Why the user arrived at /login; shown as a short notice there. */
export type SignedOutReason = 'idle' | 'expired' | 'signed_out' | 'setup_complete' | 'password_set';

export interface LoginLocationState {
  reason?: SignedOutReason;
  /** Where to return after signing in (only paths inside /app are honoured). */
  from?: string;
}

/** Puts the portal into the signed-out state: no session, and no data left from the old one. */
export function clearSession(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] });
  queryClient.setQueryData(SESSION_QUERY_KEY, null);
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: AUTH_FLOW_META,
    mutationFn: (credentials: LoginRequest) =>
      apiRequest<SessionResponse>('/auth/login', {
        method: 'POST',
        body: credentials,
        schema: sessionResponseSchema,
      }),
    onSuccess: (session) => {
      // Start the signed-in session clean, from the API's own answer.
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] });
      queryClient.setQueryData(SESSION_QUERY_KEY, session);
    },
  });
}

/**
 * Signs out through the API, then clears all session data; the authenticated layout then sends
 * the user to /login with the reason. A 401 means the
 * server session had already ended — the outcome is the same. If the request fails for another
 * reason (e.g. no network), the portal still leaves the signed-in state locally: protected content
 * is never kept on screen. The server session then ends by its own idle timeout.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    meta: AUTH_FLOW_META,
    mutationFn: async () => {
      try {
        await apiRequest('/auth/logout', { method: 'POST' });
      } catch (error) {
        if (!isUnauthenticated(error)) throw error;
      }
    },
  });

  return {
    isPending: mutation.isPending,
    logout: async (reason: SignedOutReason = 'signed_out') => {
      try {
        await mutation.mutateAsync();
      } catch {
        // Fall through: sign out locally regardless (see above).
      }
      clearSession(queryClient);
      queryClient.setQueryData(SIGNED_OUT_REASON_KEY, reason);
    },
  };
}
